import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  IGendecSettingsWire,
  IGendecStationConfig,
  firstGendecConfigError,
  validateGendecConfig,
} from "@vmosolutions/oc-interfaces";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { INotifications } from "src/API/AmplifyAPIWrapper";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { setGendecConfig, setGendecStale, updateGendecConfig } from "src/redux/actions/Gendec/Gendec.Actions";
import { StationConfigsEditor } from "src/components/Admin/Tabs/GenDecManagement/StationConfigsEditor";

interface IAirportGendecSectionProps {
  /** The currently-selected airport's ICAO `Ident`. Used as `airportFilter`. */
  airportIdent: string;
}

/**
 * Per-airport GenDec recipients section embedded in the Airport admin tab.
 *
 * Hidden entirely when the plugin isn't deployed for this customer (`loaded` is true
 * but `config` is null - same gate AdminWindow uses for the GenDec Management tab).
 */
export const AirportGendecSection = (props: IAirportGendecSectionProps): JSX.Element | null => {
  const { airportIdent } = props;
  const dispatch = useDispatch();
  const { cancellablePromise } = useCancellablePromise();
  const flightTypes = useSelector((state: IRootState) => state.structures?.customerStructure?.flightTypes ?? []);
  const stale = useSelector((state: IRootState) => state.gendec?.stale ?? false);
  const loaded = useSelector((state: IRootState) => state.gendec?.loaded ?? false);
  const snapshot = useSelector((state: IRootState) => state.gendec?.config ?? null);

  const [stationConfigs, setStationConfigs] = useState<IGendecStationConfig[]>(snapshot?.stationConfigs ?? []);
  const [dirty, setDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [reloadConfirmOpen, setReloadConfirmOpen] = useState<boolean>(false);

  // Sync local state from the Redux snapshot when not dirty, and clear the stale flag.
  useEffect(() => {
    if (snapshot && !dirty) {
      setStationConfigs(snapshot.stationConfigs ?? []);
      dispatch(setGendecStale(false));
    }
  }, [snapshot, dirty, dispatch]);

  const handleChange = useCallback((next: IGendecStationConfig[]) => {
    setStationConfigs(next);
    setDirty(true);
  }, []);

  /**
   * Apply the Redux snapshot to local state, discarding any unsaved edits. Single
   * handler for both the Reset button and the stale-banner Reload action - they're the
   * same operation now that the snapshot is kept fresh by the WS handler applying
   * broadcast payloads directly, so neither needs a GET.
   */
  const applySnapshot = useCallback((): void => {
    if (snapshot) setStationConfigs(snapshot.stationConfigs ?? []);
    setDirty(false);
  }, [snapshot]);

  /** Reload guarded by a confirm dialog when there are unsaved edits. */
  const handleStaleReload = useCallback(() => {
    if (dirty) {
      setReloadConfirmOpen(true);
      return;
    }
    applySnapshot();
  }, [dirty, applySnapshot]);

  const validationError = useMemo(() => {
    if (!snapshot) return undefined;
    const draft: IGendecSettingsWire = { ...snapshot, stationConfigs };
    return firstGendecConfigError(validateGendecConfig(draft));
  }, [snapshot, stationConfigs]);

  const handleSave = useCallback(async () => {
    if (!snapshot || validationError) return;
    const config: IGendecSettingsWire = { ...snapshot, stationConfigs };
    const notifications: INotifications = {
      dispatch,
      infoMessage: "Saving GenDec station configs",
      successMessage: "GenDec station configs saved",
      errorMessage: "Error: unable to save GenDec station configs",
    };
    setIsSaving(true);
    const saved = await updateGendecConfig(config, notifications, cancellablePromise);
    setIsSaving(false);
    if (saved) {
      // Reset dirty BEFORE dispatching so the snapshot useEffect can sync our local
      // stationConfigs to the freshly-saved value without being gated out.
      setDirty(false);
      dispatch(setGendecConfig(saved));
    }
  }, [snapshot, stationConfigs, validationError, dispatch, cancellablePromise]);

  // Hide entirely until the initial load completes and the plugin is confirmed deployed.
  if (!loaded) return null;
  if (snapshot === null) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">GenDec Recipients</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={applySnapshot}
            disabled={!dirty || isSaving}
          >
            Reset
          </Button>
          <Tooltip title={validationError ?? ""} disableHoverListener={!validationError}>
            <span>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => void handleSave()}
                disabled={!dirty || isSaving || !!validationError}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Hidden while saving so the WS echo from our own save doesn't flash the banner
          before the PUT response resolves and clears dirty. */}
      {stale && dirty && !isSaving && (
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            mb: 1,
            bgcolor: "secondary.light",
            color: "rgba(0, 0, 0, 0.87)",
            "& .MuiAlert-icon": { color: "rgba(0, 0, 0, 0.87)" },
          }}
          action={
            <Button color="inherit" size="small" onClick={handleStaleReload}>
              Reload
            </Button>
          }
        >
          Another operator updated the GenDec configuration since you opened it. Reloading will discard your unsaved
          changes.
        </Alert>
      )}

      <StationConfigsEditor
        configs={stationConfigs}
        onChange={handleChange}
        flightTypes={flightTypes}
        airportFilter={airportIdent}
      />

      <Dialog open={reloadConfirmOpen} onClose={() => setReloadConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reload GenDec configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Another operator updated the GenDec configuration. Reloading will discard your unsaved changes for{" "}
            <strong>{airportIdent}</strong>. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReloadConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setReloadConfirmOpen(false);
              applySnapshot();
            }}
          >
            Reload (discard my changes)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AirportGendecSection;
